/** Task contracts and the one task query contract — spec §6.5, §7.4, §7.6. */
import { z } from 'zod';
import { MAX_ASSIGNEES, MAX_BULK_TASKS, MAX_QUERY_LIMIT, PRIORITIES, RECURRENCE_FREQS, STATUS_CATEGORIES, TASK_TYPES, } from './enums.js';
import { csvOf, objectIdSchema, richTextSchema } from './common.js';
export const checklistItemInputSchema = z.object({
    text: z.string().min(1).max(300),
    done: z.boolean().default(false),
});
export const recurrenceSchema = z
    .object({
    freq: z.enum(RECURRENCE_FREQS),
    interval: z.number().int().min(1).max(365).default(1),
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    dayOfMonth: z.number().int().min(1).max(31).optional(),
    nthWeekday: z
        .object({
        nth: z.number().int().min(-1).max(5),
        weekday: z.number().int().min(0).max(6),
    })
        .optional(),
    endsOn: z.coerce.date().optional(),
    endsAfter: z.number().int().min(1).max(500).optional(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.freq === 'weekly' && (!val.weekdays || val.weekdays.length === 0)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['weekdays'],
            message: 'Weekly recurrence needs at least one weekday',
        });
    }
    if (val.freq === 'monthly' && !val.dayOfMonth && !val.nthWeekday) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dayOfMonth'],
            message: 'Monthly recurrence needs dayOfMonth or nthWeekday',
        });
    }
});
export const createTaskSchema = z
    .object({
    projectId: objectIdSchema,
    title: z.string().trim().min(1).max(300),
    description: richTextSchema.nullish(),
    type: z.enum(TASK_TYPES).optional(),
    statusId: z.string().min(1).max(40).optional(),
    priority: z.enum(PRIORITIES).default('P2'),
    assigneeIds: z.array(objectIdSchema).max(MAX_ASSIGNEES).default([]),
    primaryAssigneeId: objectIdSchema.nullish(),
    reviewerId: objectIdSchema.nullish(),
    watcherIds: z.array(objectIdSchema).max(50).default([]),
    parentTaskId: objectIdSchema.nullish(),
    sprintId: objectIdSchema.nullish(),
    labelIds: z.array(objectIdSchema).max(30).default([]),
    customFields: z.record(z.any()).default({}),
    estimateMinutes: z.number().int().min(0).max(100000).nullish(),
    startDate: z.coerce.date().nullish(),
    dueDate: z.coerce.date().nullish(),
    checklist: z.array(checklistItemInputSchema).max(100).default([]),
    blockedBy: z.array(objectIdSchema).max(50).default([]),
    blocks: z.array(objectIdSchema).max(50).default([]),
    recurrence: recurrenceSchema.nullish(),
    templateId: objectIdSchema.nullish(),
    sourceNoteId: objectIdSchema.nullish(),
})
    .strict()
    .superRefine((val, ctx) => {
    if (val.startDate && val.dueDate && val.dueDate < val.startDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['dueDate'],
            message: 'Due date cannot be before start date',
        });
    }
});
export const updateTaskSchema = z
    .object({
    title: z.string().trim().min(1).max(300),
    description: richTextSchema.nullish(),
    type: z.enum(TASK_TYPES),
    statusId: z.string().min(1).max(40),
    priority: z.enum(PRIORITIES),
    assigneeIds: z.array(objectIdSchema).max(MAX_ASSIGNEES),
    primaryAssigneeId: objectIdSchema.nullish(),
    reviewerId: objectIdSchema.nullish(),
    watcherIds: z.array(objectIdSchema).max(50),
    parentTaskId: objectIdSchema.nullish(),
    sprintId: objectIdSchema.nullish(),
    labelIds: z.array(objectIdSchema).max(30),
    customFields: z.record(z.any()),
    estimateMinutes: z.number().int().min(0).max(100000).nullish(),
    startDate: z.coerce.date().nullish(),
    dueDate: z.coerce.date().nullish(),
    blockedReason: z.string().max(500).nullish(),
    recurrence: recurrenceSchema.nullish(),
    rank: z.string().min(1).max(200),
})
    .strict()
    .partial();
export const changeStatusSchema = z
    .object({ statusId: z.string().min(1).max(40), reason: z.string().max(500).optional() })
    .strict();
export const moveTaskSchema = z
    .object({
    statusId: z.string().min(1).max(40).optional(),
    beforeTaskId: objectIdSchema.nullish(),
    afterTaskId: objectIdSchema.nullish(),
})
    .strict();
export const setAssigneesSchema = z
    .object({
    assigneeIds: z.array(objectIdSchema).max(MAX_ASSIGNEES),
    primaryAssigneeId: objectIdSchema.nullish(),
})
    .strict();
export const checklistCreateSchema = z
    .object({ text: z.string().min(1).max(300) })
    .strict();
export const checklistUpdateSchema = z
    .object({ text: z.string().min(1).max(300).optional(), done: z.boolean().optional() })
    .strict();
export const dependencySchema = z
    .object({ type: z.enum(['blockedBy', 'blocks']), taskId: objectIdSchema })
    .strict();
export const duplicateTaskSchema = z
    .object({
    includeSubtasks: z.boolean().default(false),
    includeChecklist: z.boolean().default(true),
    includeAttachments: z.boolean().default(false),
})
    .strict();
export const quickAddSchema = z
    .object({
    text: z.string().min(1).max(500),
    projectId: objectIdSchema.optional(),
})
    .strict();
export const myDayOrderSchema = z
    .object({ taskOrder: z.array(objectIdSchema).max(500) })
    .strict();
/* ── Bulk (§7.4) ───────────────────────────────────────── */
export const BULK_OPS = [
    'status',
    'priority',
    'assignee',
    'sprint',
    'labels',
    'dueDate',
    'archive',
    'delete',
    'move-to-sprint',
];
export const bulkTaskSchema = z
    .object({
    taskIds: z.array(objectIdSchema).min(1).max(MAX_BULK_TASKS),
    op: z.enum(BULK_OPS),
    payload: z.record(z.any()).default({}),
})
    .strict();
/* ── Query contract (§7.6) ─────────────────────────────── */
export const DUE_BUCKETS = [
    'overdue',
    'today',
    'tomorrow',
    'this_week',
    'next_week',
    'no_date',
];
export const GROUP_BYS = [
    'status',
    'assignee',
    'priority',
    'project',
    'dueWeek',
    'label',
    'type',
];
export const SORT_FIELDS = [
    'priorityScore',
    'dueDate',
    'createdAt',
    'updatedAt',
    'title',
    'rank',
    'estimate',
];
const boolish = z
    .union([z.boolean(), z.string()])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true' || v === '1'));
export const taskQuerySchema = z
    .object({
    projectId: csvOf(objectIdSchema).optional(),
    statusId: csvOf(z.string().min(1)).optional(),
    statusCategory: csvOf(z.enum(STATUS_CATEGORIES)).optional(),
    assigneeId: csvOf(z.string().min(1)).optional(), // 'me' | 'unassigned' | ObjectId
    reporterId: z.string().min(1).optional(),
    reviewerId: z.string().min(1).optional(),
    watcherId: z.string().min(1).optional(),
    priority: csvOf(z.enum(PRIORITIES)).optional(),
    type: csvOf(z.enum(TASK_TYPES)).optional(),
    labelId: csvOf(objectIdSchema).optional(),
    sprintId: z.string().min(1).optional(), // ObjectId | 'current' | 'backlog'
    parentTaskId: z.string().min(1).optional(), // ObjectId | 'none'
    dueFrom: z.coerce.date().optional(),
    dueTo: z.coerce.date().optional(),
    dueBucket: z.enum(DUE_BUCKETS).optional(),
    createdFrom: z.coerce.date().optional(),
    createdTo: z.coerce.date().optional(),
    completedFrom: z.coerce.date().optional(),
    completedTo: z.coerce.date().optional(),
    isBlocked: boolish.optional(),
    hasEstimate: boolish.optional(),
    q: z.string().max(200).optional(),
    includeArchived: boolish.default(false),
    includeSubtasks: boolish.default(true),
    groupBy: z.enum(GROUP_BYS).optional(),
    sortBy: z.enum(SORT_FIELDS).default('priorityScore'),
    sortDir: z.enum(['asc', 'desc']).default('desc'),
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(MAX_QUERY_LIMIT).default(50),
    groupLimit: z.coerce.number().int().min(1).max(MAX_QUERY_LIMIT).default(50),
    fields: z.enum(['summary', 'full']).default('summary'),
})
    .strict();
export const suggestAssigneesQuerySchema = z
    .object({
    projectId: objectIdSchema,
    type: z.enum(TASK_TYPES).optional(),
    estimateMinutes: z.coerce.number().int().min(0).optional(),
    dueDate: z.coerce.date().optional(),
})
    .strict();
/* ── All-projects board (V6-03) ────────────────────────── */
/**
 * The cross-project board's filter set.
 *
 * A narrow, deliberate subset of `taskQuerySchema` rather than the whole
 * thing: this board is drawn in status *categories*, so `statusId`,
 * `statusCategory`, `groupBy`, `sortBy` and the rest either do not apply or
 * would contradict the ordering rule §2 fixes. Sharing the wider schema would
 * have made all of them reachable and none of them meaningful.
 */
export const ALL_PROJECTS_DUE_BUCKETS = ['overdue', 'this_week'];
export const allProjectsBoardQuerySchema = z
    .object({
    projectId: csvOf(objectIdSchema).optional(),
    assigneeId: csvOf(z.string().min(1)).optional(),
    priority: csvOf(z.enum(PRIORITIES)).optional(),
    labelId: csvOf(objectIdSchema).optional(),
    dueBucket: z.enum(ALL_PROJECTS_DUE_BUCKETS).optional(),
    /** The one-click toggle. Wins over `assigneeId` when both arrive. */
    justMine: boolish.optional(),
    /** `category:token` per column — columns paginate independently. */
    cursor: csvOf(z.string().min(1)).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
})
    .strict();
/** What a user's saved filter set may contain — the same fields, persisted. */
export const allProjectsBoardFiltersSchema = allProjectsBoardQuerySchema
    .omit({ cursor: true, limit: true })
    .partial();
/** Column headings, in canonical order. */
export const STATUS_CATEGORY_LABELS = {
    todo: 'To do',
    in_progress: 'In progress',
    done: 'Done',
    cancelled: 'Cancelled',
};
//# sourceMappingURL=task.js.map